import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { apiClient } from "../../../api/apiClient";
import ContentForm from "./ContentForm";

function ContentEdit() {
  const navigate = useNavigate();
  const { category, id } = useParams();

  const [initialData, setInitialData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [errorMsg, setErrorMsg] = useState("");

  useEffect(() => {
    if (category === "calendar") {
      navigate("/stdcounciladmin/adminhome/contents/calendar", {
        replace: true,
      });
    }
  }, [category, navigate]);

  const getDetailUrl = () => {
    if (category === "notice") {
      return `/api/activity/notice/${id}`;
    }

    if (category === "event") {
      return `/api/activity/event/${id}`;
    }

    if (category === "partner") {
      return `/api/activity/partnership/${id}`;
    }

    return null;
  };

  const normalizeImages = (images = []) => {
    if (!Array.isArray(images)) return [];

    return images
      .map((image) => {
        if (typeof image === "string") {
          return {
            imageId: image,
            url: image,
            fileName: "기존 이미지",
            isExisting: true,
          };
        }

        return {
          imageId: image.imageId,
          url: image.url || image.imageUrl || image.fileUrl || "",
          fileName: image.fileName || "기존 이미지",
          isExisting: true,
        };
      })
      .filter((image) => image.url);
  };

  const normalizeData = (data) => {
    if (!data) return null;

    if (category === "notice") {
      return {
        id: data.noticeId,
        noticeId: data.noticeId,
        title: data.title || "",
        content: data.content || "",
        writer: data.createdBy || "관리자",
        date: data.createdAt || "",
        fileName: data.files?.[0]?.fileName || "",
        files: data.files || [],
        images: normalizeImages(data.images),
      };
    }

    if (category === "event") {
      return {
        id: data.eventId,
        eventId: data.eventId,
        title: data.title || data.eventName || "",
        content:
          data.introduction || data.overview || data.detailProgram || "",
        eventName: data.eventName || "",
        startDate: data.startDate || "",
        endDate: data.endDate || "",
        introduction: data.introduction || "",
        overview: data.overview || "",
        detailProgram: data.detailProgram || "",
        writer: data.createdBy || "관리자",
        date: data.createdAt || "",
        fileName: data.mainImg ? "기존 대표 이미지" : "",
        mainImg: data.mainImg || "",
        images: normalizeImages(data.images),
      };
    }

    if (category === "partner") {
      return {
        id: data.partnershipId,
        partnershipId: data.partnershipId,
        title: data.title || data.companyName || "",
        content: data.content || data.benefitSummary || "",
        companyName: data.companyName || "",
        startDate: data.startDate || "",
        endDate: data.endDate || "",
        benefitSummary: data.benefitSummary || "",
        category: data.category || "",
        durationType: data.durationType || "",
        writer: data.createdBy || "관리자",
        date: data.createdAt || "",
        fileName: data.mainImg ? "기존 대표 이미지" : "",
        mainImg: data.mainImg || "",
        images: normalizeImages(data.images),
      };
    }

    return null;
  };

  useEffect(() => {
    if (category === "calendar") {
      return;
    }

    const fetchDetail = async () => {
      const url = getDetailUrl();

      if (!url) {
        setErrorMsg("지원하지 않는 게시글 유형입니다.");
        setLoading(false);
        return;
      }

      try {
        setLoading(true);

        const result = await apiClient(url);
        const data = result?.data;

        const normalized = normalizeData(data);

        if (!normalized) {
          setErrorMsg("게시글을 찾을 수 없습니다.");
          return;
        }

        setInitialData(normalized);
      } catch (error) {
        console.error("게시글 상세 조회 오류:", error);
        setErrorMsg(error.message || "게시글을 불러오지 못했습니다.");
      } finally {
        setLoading(false);
      }
    };

    fetchDetail();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [category, id]);

  const handleListMove = () => {
    navigate(`/stdcounciladmin/adminhome/contents/${category}`, {
      replace: true,
    });
  };

  const handleCancel = () => {
    handleListMove();
  };

  const handleSaveComplete = () => {
    handleListMove();
  };

  if (category === "calendar") {
    return null;
  }

  if (loading) {
    return (
      <div className="content-form-page">
        <h2 className="content-form-title">게시글을 불러오는 중입니다.</h2>
      </div>
    );
  }

  if (!initialData) {
    return (
      <div className="content-form-page">
        <h2 className="content-form-title">
          {errorMsg || "게시글을 찾을 수 없습니다."}
        </h2>

        <div className="content-form-footer">
          <button
            type="button"
            className="content-cancel-btn"
            onClick={handleCancel}
          >
            목록으로
          </button>
        </div>
      </div>
    );
  }

  return (
    <ContentForm
      mode="edit"
      category={category}
      initialData={initialData}
      onCancel={handleCancel}
      onSaveComplete={handleSaveComplete}
    />
  );
}

export default ContentEdit;